import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type { AuthState } from './types';
import { apiService } from './services/api';

// Grow Pages
import GrowDashboard from './pages/grow/GrowDashboard';
import UnlistedStocks from './pages/grow/UnlistedStocks';
import UnlistedStockDetails from './pages/grow/UnlistedStockDetails';
import TradeSignals from './pages/grow/TradeSignals';
import MarketAction from './pages/grow/MarketAction';
import Murmurs from './pages/grow/Murmurs';

// Manage Pages
import StockNews from './pages/manage/StockNews';
import PriceAlerts from './pages/manage/PriceAlerts';

// Pages (to be created)
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import KnowMore from './pages/KnowMore';
import Settings from './pages/Settings';
import Privacy from './pages/Privacy';
import VerifyEndpoint from './pages/VerifyEndpoint';
// BROKER INTEGRATION TEMPORARILY DISABLED - See BROKER_INTEGRATION_REVERT_GUIDE.md for re-enabling
// import BrokerConnection from './pages/BrokerConnection';
// import BrokerSuccess from './pages/BrokerSuccess';
// import BrokerError from './pages/BrokerError';
import Holdings from './pages/Holdings';
import History from './pages/History';
import GrowthDashboard from './pages/admin/GrowthDashboard';
import UnlistedStocksAdmin from './pages/admin/UnlistedStocksAdmin';
import UnlistedStockEditor from './pages/admin/UnlistedStockEditor';

// Layout components (to be created)
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AnalyticsTracker from './components/AnalyticsTracker';

function App() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Check authentication status on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');

      if (!token) {
        setAuthState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        // Try to verify the current token
        const response = await apiService.auth.verifyToken();

        if (response.success && response.data) {
          setAuthState({
            user: response.data.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          // Token is invalid, clear it
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } catch (error: any) {
        console.error('Auth check failed:', error);

        // If token verification fails, try to refresh the token
        if (error.response?.status === 401) {
          try {
            const refreshResponse = await apiService.auth.refreshToken();

            if (refreshResponse.success && refreshResponse.data) {
              // Update tokens
              localStorage.setItem('authToken', refreshResponse.data.access_token);
              localStorage.setItem('refreshToken', refreshResponse.data.refresh_token);

              // Try to get user profile again
              const profileResponse = await apiService.auth.getProfile();

              if (profileResponse.success && profileResponse.data) {
                setAuthState({
                  user: profileResponse.data,
                  isAuthenticated: true,
                  isLoading: false,
                });
                return;
              }
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }

        // Clear invalid tokens
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    checkAuthStatus();
  }, []);

  // Show loading spinner while checking auth
  if (authState.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading MarketEye...</p>
        </div>
      </div>
    );
  }

  return (
    <Router basename="/">
      <AnalyticsTracker>
        <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login setAuthState={setAuthState} />} />
            <Route path="/terms" element={<Terms authState={authState} setAuthState={setAuthState} />} />
            <Route path="/contact" element={<Contact authState={authState} setAuthState={setAuthState} />} />
            <Route path="/know-more" element={<KnowMore authState={authState} setAuthState={setAuthState} />} />
            <Route path="/privacy" element={<Privacy authState={authState} setAuthState={setAuthState} />} />
            <Route path="/verify-endpoint" element={<VerifyEndpoint authState={authState} setAuthState={setAuthState} />} />

            {/* Protected routes */}
            <Route
              path="/settings"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <Settings authState={authState} />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <Dashboard />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            {/* Manage Section Routes */}
            <Route
              path="/manage/alerts"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <PriceAlerts />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/manage/news"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <StockNews />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Grow Section Routes - Public Access Allowed for Dashboard/Unlisted */}
            <Route
              path="/grow"
              element={
                <Layout authState={authState} setAuthState={setAuthState}>
                  <GrowDashboard />
                </Layout>
              }
            />
            <Route
              path="/grow/murmurs"
              element={
                <Layout authState={authState} setAuthState={setAuthState}>
                  <Murmurs />
                </Layout>
              }
            />
            <Route
              path="/grow/unlisted"
              element={
                <Layout authState={authState} setAuthState={setAuthState}>
                  <UnlistedStocks user={authState.user} />
                </Layout>
              }
            />
            <Route
              path="/grow/unlisted-stocks/:id"
              element={
                <Layout authState={authState} setAuthState={setAuthState}>
                  <UnlistedStockDetails user={authState.user} />
                </Layout>
              }
            />
            {/* Admin Routes for Unlisted Stocks */}
            <Route
              path="/admin/unlisted-stocks"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <UnlistedStocksAdmin user={authState.user} />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/unlisted-stocks/new"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <UnlistedStockEditor />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/unlisted-stocks/:id/edit"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <UnlistedStockEditor />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            {/* Protected Grow Routes */}
            <Route
              path="/grow/signals"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <TradeSignals />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/grow/market-action"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <MarketAction />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            <Route
              path="/holdings"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <Holdings />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/history"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <History />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/admin/growth"
              element={
                authState.isAuthenticated ? (
                  <Layout authState={authState} setAuthState={setAuthState}>
                    <GrowthDashboard />
                  </Layout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Redirect unknown routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AnalyticsTracker>
    </Router>
  );
}

export default App;
